import { ArrowRight, Library, Mic, Paperclip, Square } from "lucide-react";
import * as React from "react";
import { useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Spinner } from "../shared";
import Loading from "../shared/Loading";
import { Button } from "./button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";
import { WebSearchToggle } from "./web-search-toggle";

interface ChatInputProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  isSearching?: boolean;
  actionDisabled?: boolean;
  onClickButton: (
    event: React.MouseEvent<HTMLInputElement | HTMLButtonElement> |
          React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => Promise<void>;
  onAttachmentClick?: () => void;
  onPromptLibraryClick?: () => void;
  isUploading?: boolean;
  attachmentDisabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  selectedDocuments?: React.ReactNode;
  onDictateClick?: () => void;
  onDictatePrewarm?: () => void;
  isDictating?: boolean;
  isDictationConnecting?: boolean;
  dictationSupported?: boolean;
  webSearchEnabled?: boolean;
  onWebSearchChange?: (enabled: boolean) => void;
  showWebSearchToggle?: boolean;
  quotedExcerpt?: React.ReactNode;
}

const ChatInput = React.forwardRef<HTMLTextAreaElement, ChatInputProps>(
  ({
    className,
    onClickButton,
    isSearching,
    actionDisabled = false,
    onAttachmentClick,
    onPromptLibraryClick,
    isUploading = false,
    attachmentDisabled = false,
    onChange,
    selectedDocuments,
    onDictateClick,
    onDictatePrewarm,
    isDictating = false,
    isDictationConnecting = false,
    dictationSupported = false,
    webSearchEnabled = false,
    onWebSearchChange,
    showWebSearchToggle = false,
    quotedExcerpt,
    ...props
  }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const isSubmitDisabled = Boolean(actionDisabled || (props.disabled && !isSearching));

    const resizeTextarea = React.useCallback((textarea: HTMLTextAreaElement) => {
      if (!textarea.value) {
        textarea.style.height = "52px";
        return;
      }

      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }, []);

    const setTextareaRef = React.useCallback((textarea: HTMLTextAreaElement | null) => {
      textareaRef.current = textarea;

      if (textarea) {
        resizeTextarea(textarea);
      }
    }, [resizeTextarea]);

    // Combine refs
    React.useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (event) => {
      resizeTextarea(event.currentTarget);
      onChange?.(event);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
      }
    };

    // Auto-resize when value changes programmatically
    useLayoutEffect(() => {
      const currentValue = props.value;

      if (!textareaRef.current) {
        return;
      }

      if (currentValue !== undefined) {
        resizeTextarea(textareaRef.current);
        return;
      }

      resizeTextarea(textareaRef.current);
    }, [props.value, resizeTextarea]);

    const webToggleVisible = Boolean(showWebSearchToggle && onWebSearchChange);
    const showToolbar = Boolean(selectedDocuments) || webToggleVisible;

    return (
      <div className="flex flex-col gap-2">
        {/* Selected Documents and Web Toggle */}
        {showToolbar && (
        <div className="flex items-end justify-between gap-3 px-1">
          {/* Selected Documents on the left */}
          <div className="flex-1 min-w-0 overflow-hidden">
            {selectedDocuments}
          </div>

          {/* Web Toggle on the right, aligned to bottom */}
          {webToggleVisible && onWebSearchChange && (
          <div className="shrink-0 flex items-center gap-3">
            <WebSearchToggle
              enabled={webSearchEnabled}
              onChange={onWebSearchChange}
            />
          </div>
          )}
        </div>
        )}

        {/* Chat Input */}
        <div className={cn(
          "flex flex-row items-end w-full overflow-hidden rounded-xl bg-white shadow-xs transition-all duration-300 ease-in-out",
          "border border-slate-300 focus-within:border-slate-400/80 focus-within:ring-2 focus-within:ring-slate-100 focus-within:shadow-xs"
        )}>
          <div className="flex-1 min-w-0 flex flex-col">
            {quotedExcerpt}
            <textarea
              className={cn(
                "h-[52px] p-3 pt-4 min-h-[52px] max-h-[150px] w-full resize-none overflow-y-auto",
                "bg-transparent text-slate-800 placeholder:text-slate-400 text-sm",
                "focus:outline-hidden focus:ring-0 focus:border-transparent",
                className
              )}
              ref={setTextareaRef}
              rows={1}
              onChange={handleChange}
              onKeyDown={(e) => {
                handleKeyDown(e);
                if (props.onKeyDown) {
                  props.onKeyDown(e);
                }
              }}
              placeholder="Ask me about your case..."
              {...props}
            />
          </div>

          <div className={cn(
            "flex items-center self-stretch px-3 py-2"
          )}>
            {onAttachmentClick && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className={cn(
                            "h-8 w-8 mr-1 rounded-full text-slate-500 hover:text-slate-700 hover:bg-slate-100 relative",
                            attachmentDisabled && "opacity-50 cursor-not-allowed hover:bg-transparent hover:text-slate-500"
                          )}
                          onClick={onAttachmentClick}
                          disabled={attachmentDisabled}
                        >
                          <div className="relative flex items-center justify-center">
                            <Paperclip className="h-4 w-4 relative z-10" />
                            {isUploading && (
                              <Loading />
                            )}
                          </div>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        Add files to this conversation
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {onPromptLibraryClick && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 mr-1 rounded-full text-slate-500 hover:text-slate-700 hover:bg-slate-100 relative"
                          onClick={onPromptLibraryClick}
                        >
                          <Library className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        Prompt Library
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {onDictateClick && dictationSupported && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className={cn(
                            "h-8 w-8 mr-1 rounded-full relative",
                            isDictating
                              ? "text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700"
                              : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                          )}
                          onClick={onDictateClick}
                          onPointerEnter={onDictatePrewarm}
                          onFocus={onDictatePrewarm}
                          aria-label={isDictating ? "Stop voice dictation" : "Start voice dictation"}
                          aria-pressed={isDictating}
                        >
                          {isDictationConnecting ? (
                            <Spinner />
                          ) : (
                            <Mic className={cn("h-4 w-4", isDictating && "animate-pulse")} />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        {isDictating ? "Stop dictation" : "Dictate"}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-8 w-8 shrink-0 rounded-full flex items-center justify-center transition-colors",
                    isSearching
                      ? "bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-800 active:scale-95"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-700",
                    isSubmitDisabled && "opacity-50 cursor-not-allowed hover:bg-slate-100"
                  )}
                  onClick={(event) => onClickButton(event)}
                  disabled={isSubmitDisabled}
                  aria-label={isSearching ? (actionDisabled ? "Preparing response" : "Stop generating") : "Send message"}
                  title={isSearching ? (actionDisabled ? "Preparing response" : "Stop generating") : "Send message"}
                >
                  {isSearching ? (
                    <Square className="h-3 w-3 fill-current stroke-[3]" />
                  ) : (
                    <ArrowRight className="h-4 w-4" />
                  )}
                </Button>
          </div>
        </div>
      </div>
    );
  }
);
ChatInput.displayName = "ChatInput";

export { ChatInput };
